import { extractBandOrder } from './assessmentService';
import { MarkingRunData } from '../types';
import { 
    NUMBER_OF_MARKING_RUNS,
    DEFAULT_SYSTEM_INSTRUCTIONS,
    QUOTA_EXCEEDED_USER_MESSAGE
} from '../constants';

// Get API base URL based on environment
const getApiBaseUrl = (): string => {
  // Check if we're in production
  if (import.meta.env.PROD) {
    // Use production API URL - you'll need to set this in your environment variables
    return import.meta.env.VITE_API_BASE_URL || 'https://us-central1-history-essay-bot.cloudfunctions.net/api';
  }
  // Development/local environment
  return 'http://localhost:3001';
};

// OpenAI API configuration - now using secure backend proxy
const getOpenAIProxyUrl = (): string => `${getApiBaseUrl()}/api/openai`;

// Safety check for proxy server
const API_INITIALIZATION_ERROR = "OPENAI_PROXY_UNAVAILABLE::The OpenAI proxy server is not available. Please ensure it's running.";

/**
 * Centralized error handler for OpenAI API calls
 */
function handleOpenAIError(error: any, context: string, rawResponseText = '') {
    const errorMessage = error instanceof Error ? error.message : String(error);

    // Handle proxy server unavailable
    if (errorMessage.includes("Failed to fetch") || errorMessage.includes("ECONNREFUSED")) {
        console.error(`OpenAI proxy server error during ${context}:`, error);
        throw new Error("OPENAI_PROXY_UNAVAILABLE::The OpenAI proxy server is not available. Please ensure it's running on port 3001.");
    }

    // Handle API key errors from proxy
    if (errorMessage.includes("API_KEY_INVALID") || errorMessage.includes("authentication")) {
        console.error(`Critical OpenAI Error during ${context}: The API key is invalid or missing.`, error);
        throw new Error("OPENAI_API_KEY_INVALID::The OpenAI API key is not configured on the server. Please verify the server configuration.");
    }

    // Pass through user-friendly quota message
    if (error instanceof Error && error.message === QUOTA_EXCEEDED_USER_MESSAGE) {
        throw error;
    }

    // Handle JSON parsing errors
    if (error instanceof SyntaxError && error.message.toLowerCase().includes("json")) {
        console.error(`JSON Parsing Error during ${context}. Raw API response was:`, rawResponseText);
        throw new Error(`Failed to parse OpenAI response as JSON during ${context}. Check the console for the raw response.`);
    }

    // Handle rate limiting
    if (errorMessage.includes("rate_limit_exceeded") || errorMessage.includes("429")) {
        throw new Error(QUOTA_EXCEEDED_USER_MESSAGE);
    }

    // Generic fallback
    console.error(`Error during ${context}:`, error);
    throw new Error(`An error occurred during ${context}. Details: ${errorMessage}`);
}

/**
 * Retry logic for OpenAI API calls
 */
async function callOpenAIWithRetry(
    apiCallFn: () => Promise<any>,
    maxRetries = 3,
    initialDelayMs = 1000
) {
    let attempts = 0;
    let currentDelayMs = initialDelayMs;

    while (attempts < maxRetries) {
        try {
            return await apiCallFn();
        } catch (error: any) {
            attempts++;
            const isRateLimit = error.message?.includes("rate_limit") || error.message?.includes("429");
            console.warn(`OpenAI API call attempt ${attempts} failed. Is Rate Limit: ${isRateLimit}. Error: ${error.message}`);

            if (isRateLimit && attempts < maxRetries) {
                console.log(`Retrying after ${currentDelayMs}ms delay due to rate limiting...`);
                await new Promise(resolve => setTimeout(resolve, currentDelayMs));
                currentDelayMs *= 2;
            } else {
                throw error;
            }
        }
    }
    throw new Error("OpenAI API call failed after multiple retries.");
}

/**
 * Make API call to OpenAI through authenticated proxy
 * SECURITY: Now requires Firebase authentication token
 */
async function callOpenAI(messages: any[], model: string, temperature: number = 0.0, responseFormat?: any) {
    // Get Firebase auth token
    const { auth } = await import('./firebase');
    const user = auth.currentUser;
    if (!user) {
        throw new Error('User must be authenticated to use AI services');
    }
    const token = await user.getIdToken();

    const requestBody: any = {
        model,
        messages,
        temperature,
        maxTokens: 4000,
    };

    if (responseFormat) {
        requestBody.responseFormat = responseFormat;
    }

    const response = await fetch(getOpenAIProxyUrl(), {
        method: 'POST',
        headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestBody),
    });

    if (!response.ok) {
        const errorData = await response.text();
        throw new Error(`OpenAI proxy error: ${response.status} ${response.statusText}: ${errorData}`);
    }

    const data = await response.json();
    return data.choices[0].message.content;
}

/**
 * A highly resilient function to find and parse a JSON object from a string.
 * It handles markdown fences, conversational text, and other AI response variations.
 * Based on Gemini's robust parser but adapted for OpenAI responses.
 * @param {string} text - The raw text response from OpenAI.
 * @returns {object} The parsed JSON object.
 * @throws {SyntaxError} If a valid JSON object cannot be found or parsed.
 */
function robustOpenAIJsonParser(text: any) {
    if (typeof text !== 'string') {
        throw new SyntaxError("Input to parser must be a string.");
    }
    let jsonStr = text.trim();

    // Strategy 1: Find a JSON block within markdown fences (e.g., ```json ... ```)
    const fenceMatch = jsonStr.match(/```(?:json)?\s*([\s\S]*?)\s*```/);
    if (fenceMatch && fenceMatch[1]) {
        jsonStr = fenceMatch[1].trim();
    } else {
        // Strategy 2: If no fences, find the main JSON object or array.
        // It looks for the content between the first '{' or '[' and the last '}' or ']'.
        const firstBracket = jsonStr.indexOf('{');
        const firstSquare = jsonStr.indexOf('[');
        let firstIndex = -1;

        if (firstBracket === -1) {
            firstIndex = firstSquare;
        } else if (firstSquare === -1) {
            firstIndex = firstBracket;
        } else {
            firstIndex = Math.min(firstBracket, firstSquare);
        }

        if (firstIndex !== -1) {
            const lastCurly = jsonStr.lastIndexOf('}');
            const lastSquare = jsonStr.lastIndexOf(']');
            const lastIndex = Math.max(lastCurly, lastSquare);

            if (lastIndex > firstIndex) {
                jsonStr = jsonStr.substring(firstIndex, lastIndex + 1);
            }
        }
    }

    // Now that we have a candidate string, clean and parse it.
    
    // Fix missing comma between correctly closed objects, even across newlines.
    let cleanedJsonStr = jsonStr.replace(/\}\s*(?=[\r\n\s]*\{)/g, '}, {');
    
    // Remove trailing commas before a closing bracket or brace.
    cleanedJsonStr = cleanedJsonStr.replace(/,(?=\s*?[}\]])/g, '');

    try {
        return JSON.parse(cleanedJsonStr);
    } catch (e: any) {
        console.error("Final attempt to parse cleaned JSON failed. Original text:", text);
        console.error("Cleaned string was:", cleanedJsonStr);
        throw new SyntaxError(`Failed to parse JSON even after cleaning. Reason: ${e?.message || 'Unknown error'}`);
    }
}

/**
 * Check if error is a JSON parsing error
 */
function isJsonParsingError(error: any) {
  if (error instanceof SyntaxError) {
    return error.message.toLowerCase().includes("json");
  }
  
  const errorMessage = error instanceof Error ? error.message : String(error);
  return errorMessage.toLowerCase().includes("failed to parse") ||
         errorMessage.toLowerCase().includes("json") ||
         errorMessage.toLowerCase().includes("syntax");
}

/**
 * Parse JSON from OpenAI response - uses the robust parser
 */
function parseOpenAIJson(text: string): any {
    return robustOpenAIJsonParser(text);
}

/**
 * Analyze pasted image using OpenAI Vision
 */
export const analyzePastedImage = async (imageBase64: string) => {
    if (!imageBase64) throw new Error("No image data provided for analysis.");

    const messages = [
        {
            role: "user",
            content: [
                {
                    type: "text",
                    text: "Analyze this image. Your primary goal is to extract information useful for an essay. " +
                          "1. First, determine if the image is primarily textual (like a scanned document, a screenshot of text) or pictorial (like a photograph, chart, or drawing). " +
                          "2. If it is primarily textual, transcribe all the text accurately. Preserve paragraph structure. If no text is discernible, state that clearly. " +
                          "3. If it is primarily pictorial, provide a detailed description of the image's content. Focus on elements that would be relevant to a historical or analytical essay. " +
                          "4. If it's a mix, describe the visual elements and transcribe any significant text. " +
                          "Present your analysis clearly."
                },
                {
                    type: "image_url",
                    image_url: {
                        url: `data:image/jpeg;base64,${imageBase64}`
                    }
                }
            ]
        }
    ];

    try {
        return await callOpenAIWithRetry(() => 
            callOpenAI(messages, "gpt-4o-mini", 0.0)
        );
    } catch (error) {
        handleOpenAIError(error, 'pasted image analysis');
    }
};

/**
 * Transcribe image using OpenAI Vision
 */
export const transcribeImage = async (imageBase64: string) => {
    if (!imageBase64) throw new Error("No image data provided for transcription.");

    const messages = [
        {
            role: "user",
            content: [
                {
                    type: "text",
                    text: "Transcribe all handwritten and printed text in this image accurately. Preserve paragraph structure if possible. If the image contains no discernible text, respond with 'NO_TEXT_DETECTED'."
                },
                {
                    type: "image_url",
                    image_url: {
                        url: `data:image/jpeg;base64,${imageBase64}`
                    }
                }
            ]
        }
    ];

    try {
        const response = await callOpenAIWithRetry(() => 
            callOpenAI(messages, "gpt-4o-mini", 0.0)
        );
        
        if (response.trim().toUpperCase() === 'NO_TEXT_DETECTED') {
            return "No text was detected in this image page.";
        }
        return response;
    } catch (error) {
        handleOpenAIError(error, 'image transcription');
    }
};

/**
 * Mark essay using OpenAI
 */
export const markEssay = async (
  transcribedText: string,
  systemInstructions: string,
  markingRubric: string,
  assessmentQuestion: string,
  yearLevel: string,
  modelConfig: any,
  exemplars: any[] = [],
  essayInputType: string,
  assessmentType: string | null = null,
  examCondition: string | null = null,
  minWordCount: number | null = null,
  maxWordCount: number | null = null,
  onStatusUpdate?: (message: string) => void,
  retryAttempt: number = 0
): Promise<any> => {
    let exemplarBlock = '';
    if (exemplars && exemplars.length > 0) {
        exemplarBlock += "\n\n### MARKED EXEMPLARS FOR REFERENCE\n";
        exemplarBlock += "Use these as a guide for calibrating your marking against the rubric.\n\n";
        exemplars.forEach((ex: any, i: number) => {
            exemplarBlock += `--- Exemplar ${i + 1} ---\n`;
            exemplarBlock += `**Year Level:** ${ex.yearLevel || 'Not specified'}\n`;
            exemplarBlock += `**Essay Text:**\n${ex.essayText}\n\n`;
            exemplarBlock += `**Marks Given:**\n`;
            ex.criteriaBands.forEach((cb: any) => {
                exemplarBlock += `- ${cb.name}: ${cb.band}\n`;
            });
            exemplarBlock += `**Overall Score:** ${ex.overallScore}\n`;
            if (ex.explanation) {
                exemplarBlock += `**Explanation:** ${ex.explanation}\n`;
            }
            exemplarBlock += `--- End Exemplar ${i + 1} ---\n\n`;
        });
    }

    let transcriptionCaveat = '';
    if (essayInputType === 'images') {
        transcriptionCaveat = `
        
        **Special Transcription Consideration:** As this essay was transcribed from images, you MUST be forgiving of awkward sentences, grammatical errors, or unfinished phrases. These are likely transcription artifacts. Evaluate the underlying ideas, not the literal text quality.`;
    }

    // Assessment context
    let assessmentContext = '';
    if (assessmentType) {
        assessmentContext += `\n    - Assessment Type: ${assessmentType}`;
    }
    if (examCondition) {
        assessmentContext += `\n    - Exam Condition: ${examCondition}`;
    }
    if (minWordCount || maxWordCount) {
        assessmentContext += `\n    - Expected Word Count: ${minWordCount || 'No minimum'} - ${maxWordCount || 'No maximum'} words`;
    }

    const prompt = `
        Role: You are an Australian Highschool History teacher. You are fair in your marking approach and consider a students age and year level in the marking your conduct so that grades are applied appropriate to the expectations of students at their levels.

        **ASSESSMENT DETAILS:**
        - Year Level: ${yearLevel || 'Not specified'}
        - Essay Question: ${assessmentQuestion}${assessmentContext}

        **MARKING RUBRIC:**
        ${markingRubric}
        ${exemplarBlock}

        **ESSAY TO MARK:**
        \`\`\`
        ${transcribedText}
        \`\`\`

        Task:
        Step 1 
        You should start by planning out the marking process you will take on this essay. Review the marking criterion, review the type of assessment response by looking at the attributes of the assessment and expected word count range. Consider yourself a competent and fair high school Australian history teacher and reason through how you would effectively and fairly grade this response.

        Step 2
        Start by reading the whole response and get a general sense of the quality of the response. Consider how it's length by word count compared to the expected range.
        Consider the depth of the response and how well structured, coherent, accurate and (most importantly) relevant the response is to the question. You don't consider or penalise for spelling errors or small grammatical errors. Form an overall impression of the quality of the response and level of understanding the student is clearly demonstrating in terms of their understanding of the question, understanding of the topic, their historical knowledge and competence as a student. Use this general impression to guide how you determine which band descriptors best apply for each marking criterion and which mark you will award for each band.${transcriptionCaveat}

        Step 3
        Review the first marking criterion and carefully review each band descriptor one by one, and evaluate how much that band descriptor describes the response. Create a match score out of 100 and a confidence score out of 100 for how much the descriptor describes the response and your confidence level in your matching score. Select the band descriptor that has the highest match score and write as a 2 sentence justification as to why that band descriptor is the best fit and match to the response.
        
        Additionally, for the selected band, provide a 3 sentence justification explaining why you DIDN'T select the band descriptor immediately above (if one exists) and why you DIDN'T select the band descriptor immediately below (if one exists). This will help teachers understand why the response didn't meet the higher standard or why it exceeded the lower standard.

        Step 4
        Assign a score for your selected band that is between the range for that band descriptor. Use your matching scores and confidence scores across all the band descriptors to determine if you award on the higher end of the range for that band or the lower end. For example, if it fully met one band, but also partially met the band above, then consider a higher score on the range for the selected band. 
        Go through the rest of the remaining marking criterion following this process.
        Note that I don't want you to hold back in awarding top marks when a student response is out standing. I also want to make sure you don't shy away from giving responses the lowest band when marking a response when it's lacking serious depth of understanding and has very limited sophistication.

        Step 5
        Work out an overall mark for the essay by adding together all of the scores for each criterion according to the respective weighting of each criterion. The weighting is what determines how much of that criterion's score contributes to the overall mark.

        Additionally, provide comprehensive feedback for the student that includes:
        - What they did well (with specific examples from their essay)
        - Areas for improvement with detailed, actionable advice on how to enhance their analysis, evidence use, and argument structure
        - Specific suggestions that would elevate the quality of their historical writing

        **RESPONSE FORMAT:**
        Respond with a valid JSON object only:
        {
            "markingPlan": "Step 1: Your planning explanation for how you will approach marking this assessment",
            "generalImpression": "Step 2: Your overall assessment of the essay quality, structure, and student understanding",
            "criteriaResults": [
                {
                    "criterionName": "Name of Criterion 1",
                    "bandAnalysis": [
                        {"bandName": "21-25", "matchScore": 85, "confidenceScore": 90},
                        {"bandName": "16-20", "matchScore": 40, "confidenceScore": 75}
                    ],
                    "selectedBand": "21-25",
                    "justification": "Two sentence explanation of why this band descriptor is the best fit for this response.",
                    "justificationBandAbove": "Three sentence explanation of why the band above was NOT selected (null if no band above exists).",
                    "justificationBandBelow": "Three sentence explanation of why the band below was NOT selected (null if no band below exists).",
                    "score": 23
                }
            ],
            "overallEssayScore": 18,
            "detailedFeedback": {
                "strengths": "What the student did well, with specific examples from their essay",
                "improvementAreas": "Detailed actionable advice on how to elevate the essay quality, focusing on sophisticated analysis, comprehensive evidence use, and enhanced structure", 
                "specificSuggestions": "Concrete examples of how they could have enhanced specific parts of their essay"
            }
        }
    `;

    const messages = [
        {
            role: "system",
            content: systemInstructions || DEFAULT_SYSTEM_INSTRUCTIONS
        },
        {
            role: "user",
            content: prompt
        }
    ];

    let rawResponse = '';
    try {
        if (onStatusUpdate) {
            const attemptText = retryAttempt > 0 ? ` (Retry ${retryAttempt}/3)` : '';
            onStatusUpdate(`Analyzing essay with OpenAI${attemptText}...`);
        }

        rawResponse = await callOpenAIWithRetry(() => 
            callOpenAI(messages, modelConfig.apiModel, 0.0, { type: "json_object" })
        );
        
        const parsedResult = parseOpenAIJson(rawResponse);
        return { ...parsedResult, prompt };
    } catch (error) {
        // Check if this is a JSON parsing error and we haven't exhausted retries
        if (isJsonParsingError(error) && retryAttempt < 3) {
            console.warn(`JSON parsing failed on attempt ${retryAttempt + 1}, retrying...`, error);
            if (onStatusUpdate) {
                onStatusUpdate(`JSON parsing failed, retrying (${retryAttempt + 1}/3)...`);
            }
            // Wait a short time before retrying
            await new Promise(resolve => setTimeout(resolve, 1000));
            
            // Retry with incremented attempt counter
            return markEssay(
                transcribedText,
                systemInstructions,
                markingRubric,
                assessmentQuestion,
                yearLevel,
                modelConfig,
                exemplars,
                essayInputType,
                assessmentType,
                examCondition,
                minWordCount,
                maxWordCount,
                onStatusUpdate,
                retryAttempt + 1
            );
        }
        
        handleOpenAIError(error, 'essay marking', rawResponse);
    }
};

/**
 * Generate feedback using OpenAI
 */
export const generateFeedback = async (
    transcribedText: string,
    systemInstructions: string,
    markingRubric: string,
    assessmentQuestion: string,
    finalConsolidatedResults: any,
    finalOverallScore: number,
    finalOverallBand: string,
    allMarkingRunsData: any,
    yearLevel: string,
    modelConfig: any,
    consolidatedAuditData: any
) => {
    const messages = [
        {
            role: "system",
            content: "You are an expert history teacher providing constructive feedback to students."
        },
        {
            role: "user",
            content: `
                Generate comprehensive feedback for a student's essay in Markdown format.
                
                **Student's Assessment:**
                - Overall Score: ${finalOverallScore}
                - Overall Band: ${finalOverallBand}
                - Year Level: ${yearLevel}
                - Question: ${assessmentQuestion}
                
                **Essay Text:**
                \`\`\`
                ${transcribedText}
                \`\`\`
                
                **Assessment Results:**
                ${JSON.stringify(finalConsolidatedResults, null, 2)}
                
                Structure your feedback with:
                1. **What You've Done Well** - Highlight specific strengths
                2. **Areas for Improvement** - Provide actionable advice
                3. End with encouraging summary
                
                Use specific quotes from the essay to support your points.
            `
        }
    ];

    try {
        return await callOpenAIWithRetry(() => 
            callOpenAI(messages, modelConfig.apiModel, 0.6)
        );
    } catch (error) {
        handleOpenAIError(error, 'feedback generation');
    }
};
